import React, { useState, useEffect } from 'react';
import { initCountry } from './utils/countryDetect.js';
import { BrowserRouter, Routes, Route, Link } from 'react-router-dom';
import Navbar from './components/Navbar';
import HomePage from './pages/HomePage';
import ProductsPage from './pages/ProductsPage';
import ProductDetailPage from './pages/ProductDetailPage';
import CartPage from './pages/CartPage';
import CheckoutPage from './pages/CheckoutPage';
import OrderSuccessPage from './pages/OrderSuccessPage';
import BrandsPage from './pages/BrandsPage';
import AccountPage from './pages/AccountPage';
import LoginModal from './components/LoginModal';
import Logo from './components/Logo';
import { useCart } from './hooks/useCart';
import { useToast } from './hooks/useToast';
import { useAuthProvider, AuthContext } from './hooks/useAuth';
import shopApi from './services/shopApi';
import BlogList       from './pages/marketing/BlogList';
import BlogDetail     from './pages/marketing/BlogDetail';
import PrivacyPage    from './pages/marketing/PrivacyPage';
import TermsPage      from './pages/marketing/TermsPage';
import AboutPage      from './pages/marketing/AboutPage';
import HelpCenterPage from './pages/marketing/HelpCenterPage';
import ReturnsPage    from './pages/marketing/ReturnsPage';
import ShippingPage   from './pages/marketing/ShippingPage';
import CareersPage    from './pages/marketing/CareersPage';

/* ─── Footer ──────────────────────────────────────────────────────
 * Each link now has a real target:
 *   - internal routes use <Link to="/...">
 *   - external URLs (mailto, tel, social) use <a href>
 *   - links that point nowhere yet are marked with `disabled: true`
 *     and render as muted non-clickable text (no false affordance)
 * ────────────────────────────────────────────────────────────────── */
const FOOTER_COLUMNS = [
  {
    title: 'Shop',
    links: [
      { label: 'All Products', to: '/products' },
      { label: 'Brands',       to: '/brands' },
      { label: 'Categories',   to: '/products' },        // alias for now
      { label: 'New Arrivals', to: '/products?sort=new' },
    ],
  },
  {
    title: 'Company',
    links: [
      { label: 'About',    to: '/about' },
      { label: 'Blog',     to: '/blog' },
      { label: 'Careers',  to: '/careers' },
      { label: 'Contact',  href: 'mailto:info@bestskyamerican.com' },
    ],
  },
  {
    title: 'Support',
    links: [
      { label: 'Help Center', to: '/help' },
      { label: 'Returns',     to: '/returns' },
      { label: 'Shipping',    to: '/shipping' },
      { label: 'Privacy',     to: '/privacy' },
      { label: 'Terms',       to: '/terms' },
    ],
  },
];

const FooterLink = ({ item }) => {
  const base = {
    fontSize: 13,
    color: item.disabled ? 'rgba(224,240,240,0.2)' : 'rgba(224,240,240,0.55)',
    marginBottom: 8,
    cursor: item.disabled ? 'default' : 'pointer',
    transition: 'color .25s',
    textDecoration: 'none',
    display: 'block',
  };

  if (item.disabled) {
    return (
      <span style={base} title="Coming soon">
        {item.label}
      </span>
    );
  }

  if (item.href) {
    return (
      <a href={item.href} style={base}
        onMouseEnter={e => e.currentTarget.style.color = '#00E5CC'}
        onMouseLeave={e => e.currentTarget.style.color = 'rgba(224,240,240,0.55)'}>
        {item.label}
      </a>
    );
  }

  return (
    <Link to={item.to} style={base}
      onMouseEnter={e => e.currentTarget.style.color = '#00E5CC'}
      onMouseLeave={e => e.currentTarget.style.color = 'rgba(224,240,240,0.55)'}>
      {item.label}
    </Link>
  );
};

const Footer = () => (
  <footer style={{borderTop:'1px solid rgba(0,229,204,0.06)',padding:'48px 40px 32px',marginTop:'auto'}}>
    <div style={{maxWidth:1200,margin:'0 auto'}}>
      <div style={{display:'grid',gridTemplateColumns:'1.4fr 2fr',gap:48,marginBottom:40}}>
        <div>
          <div style={{marginBottom:12}}>
            <Logo iconSize={30} badge="SHOP" />
          </div>
          <div style={{fontSize:13,color:'rgba(224,240,240,0.4)',lineHeight:1.6,maxWidth:280}}>
            Shop with confidence. Every product on JepTags is guaranteed 100% authentic.
          </div>
        </div>
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:24}}>
          {FOOTER_COLUMNS.map(col => (
            <div key={col.title}>
              <div style={{fontSize:11,fontWeight:700,letterSpacing:'1px',textTransform:'uppercase',color:'rgba(224,240,240,0.4)',marginBottom:12}}>
                {col.title}
              </div>
              {col.links.map(item => (
                <FooterLink key={item.label} item={item} />
              ))}
            </div>
          ))}
        </div>
      </div>
      <div style={{borderTop:'1px solid rgba(0,229,204,0.06)',paddingTop:24,display:'flex',justifyContent:'space-between',alignItems:'center',flexWrap:'wrap',gap:12}}>
        <div style={{fontSize:12,color:'rgba(224,240,240,0.2)'}}>© 2026 JepTags. All rights reserved.</div>
        <div style={{display:'flex',gap:20}}>
          <Link to="/privacy" style={{fontSize:12,color:'rgba(224,240,240,0.4)',textDecoration:'none'}}>Privacy</Link>
          <Link to="/terms" style={{fontSize:12,color:'rgba(224,240,240,0.4)',textDecoration:'none'}}>Terms</Link>
          <Link to="/blog" style={{fontSize:12,color:'rgba(224,240,240,0.4)',textDecoration:'none'}}>Blog</Link>
        </div>
      </div>
    </div>
  </footer>
);

function AppInner() {
  const auth = useAuthProvider();
  const { cart, setCart, totals, addToCart, updateItem, removeItem } = useCart();
  const { toast, showToast } = useToast();
  const [loginModal, setLoginModal] = useState(false);
  const [loginModalTab, setLoginModalTab] = useState('login');

  // Detect visitor country ONCE on app boot — cached in localStorage for 24h
  // Without this, shopApi never knows the country and always falls back to GL
  useEffect(() => { initCountry(); }, []);

  const handleAdd = async (product) => {
    // Support both full product object and plain productId string
    const listingId = product?.listingId || null;
    const productId = product?.productId || product?.masterProductId || (typeof product === 'string' ? product : null);

    // FIX: was addToCart(productId, 1, null, listingId) which passed listingId
    // as the `country` param — wrong order. Correct signature:
    // addToCart(id, quantity, variantId, country, isListingId)
    const ok = listingId
      ? await addToCart(listingId, 1, null, null, true)   // listingId path
      : await addToCart(productId, 1, null, null, false);  // productId path

    showToast(ok ? '✓ Added to cart' : '✕ Could not add item', !ok);
  };

  const handleOrderSuccess = () => {
    setCart({ items: [], subtotal: 0, totalItems: 0 });
  };

  const openLogin = (tab = 'login') => {
    setLoginModalTab(tab);
    setLoginModal(true);
  };

  return (
    <AuthContext.Provider value={{ ...auth, openLogin }}>
      <BrowserRouter>
        <div style={{minHeight:'100vh',display:'flex',flexDirection:'column'}}>
          <Navbar
            cartCount={cart.totalItems || 0}
            consumer={auth.consumer}
            onLoginClick={() => openLogin('login')}
          />
          <main style={{flex:1}}>
            <Routes>
              <Route path="/" element={<HomePage onAdd={handleAdd} />} />
              <Route path="/products" element={<ProductsPage onAdd={handleAdd} />} />
              <Route path="/products/:productId" element={<ProductDetailPage onAdd={handleAdd} />} />
              <Route path="/brands" element={<BrandsPage />} />
              <Route path="/cart" element={<CartPage cart={cart} totals={totals} onUpdate={updateItem} onRemove={removeItem} />} />
              <Route path="/checkout" element={<CheckoutPage cart={cart} totals={totals} onOrderSuccess={handleOrderSuccess} consumer={auth.consumer} onLoginClick={() => openLogin('login')} />} />
              <Route path="/order-success" element={<OrderSuccessPage />} />
              <Route path="/account" element={<AccountPage />} />
              <Route path="/blog" element={<BlogList />} />
              <Route path="/blog/:slug" element={<BlogDetail />} />
              <Route path="/privacy" element={<PrivacyPage />} />
              <Route path="/terms" element={<TermsPage />} />
              <Route path="/about" element={<AboutPage />} />
              <Route path="/help" element={<HelpCenterPage />} />
              <Route path="/returns" element={<ReturnsPage />} />
              <Route path="/shipping" element={<ShippingPage />} />
              <Route path="/careers" element={<CareersPage />} />
            </Routes>
          </main>
          <Footer />
          <div className={`toast ${toast.visible ? 'show' : ''} ${toast.error ? 'error' : ''}`}>
            {toast.message}
          </div>
          {loginModal && (
            <LoginModal
              defaultTab={loginModalTab}
              onClose={() => setLoginModal(false)}
              onSuccess={async (token) => {
                // Merge guest cart into user account immediately after login
                if (token) {
                  const result = await shopApi.mergeCart(token);
                  if (result?.success && result?.merged && result?.data?.cart) {
                    setCart(result.data.cart);
                  }
                }
                showToast('✓ Welcome to JepTags!');
                setLoginModal(false);
              }}
            />
          )}
        </div>
      </BrowserRouter>
    </AuthContext.Provider>
  );
}

export default function App() {
  return <AppInner />;
}